import React, { useState, cloneElement, Component } from 'react';
import { motion } from 'framer-motion';
import { SparkleEffect } from '../components/SparkleEffect';
import { PlaneIcon, MapIcon, CompassIcon, SparklesIcon } from 'lucide-react';
import { GlitterHeart } from '../components/GlitterHeart';
export function TravelPage() {
  const [formData, setFormData] = useState({
    name: '',
    contactPref: '',
    contactDetails: '',
    destination: '',
    tripType: '',
    budget: '',
    dates: '',
    travelers: '',
    budgetBased: '',
    priorities: '',
    extraDetails: ''
  });
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const subject = encodeURIComponent('Travel Request - Trippin with Mandy');
    const body = encodeURIComponent(`
Name: ${formData.name}
Contact Preference: ${formData.contactPref}
Contact Details: ${formData.contactDetails}
Destination: ${formData.destination}
Trip Type: ${formData.tripType}
Budget: ${formData.budget}
Dates: ${formData.dates}
Travelers: ${formData.travelers}
Budget-based destination choice: ${formData.budgetBased}
Priorities: ${formData.priorities}
Extra Details: ${formData.extraDetails}
    `.trim());
    window.location.href = `mailto:trippinwithmandy@gmail.com?subject=${subject}&body=${body}`;
  };
  return <div className="min-h-screen bg-white pt-24 pb-12" data-id="element-647">
      {/* Hero */}
      <section className="relative py-20 text-center overflow-hidden bg-gradient-to-b from-sky-50 to-white" data-id="element-648">
        <SparkleEffect count={90} data-id="element-649" />
        <div className="max-w-3xl mx-auto px-4 relative z-10" data-id="element-650">
          <motion.h1 initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="font-heading text-5xl md:text-6xl font-bold text-plum-700 mb-6" data-id="element-651">
            Travel with <span className="shimmer-text" data-id="element-652">Mandy</span>
          </motion.h1>
          <motion.p initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} className="text-xl text-plum-500 font-medium" data-id="element-653">
            Because everybody deserves to get the hell out sometimes.
          </motion.p>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-654">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="prose prose-lg prose-plum mx-auto text-plum-700 leading-relaxed text-center" data-id="element-655">
          <p className="text-2xl font-heading text-plum-700 mb-8 font-bold" data-id="element-656">
            I believe travel is healing.
          </p>
          <p className="mb-6 font-medium" data-id="element-657">
            Getting out of your everyday environment, seeing something new,
            breathing different air — it changes you. It resets your nervous
            system and reminds you that the world is bigger than whatever you're
            going through right now.
          </p>
          <p className="mb-6 font-medium" data-id="element-658">
            I don't care if your budget is $200 or $2,000. I will find you
            something. A weekend getaway, a road trip, a beach somewhere —
            everyone deserves to escape.
          </p>
          <p className="text-xl text-cta-500 font-bold mt-8" data-id="element-659">
            Tell me your budget, tell me your dream, and let me make it happen.
          </p>
        </motion.div>
      </section>

      {/* How It Works */}
      <section className="py-14 bg-blush-50" data-id="element-660">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-661">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-16" data-id="element-662">
            How It Works
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative" data-id="element-663">
            <div className="hidden md:block absolute top-12 left-[15%] right-[15%] h-0.5 bg-blush-200" data-id="element-664" />

            {[{
            icon: <MapIcon data-id="element-665" />,
            title: '1. Book Your Free Consultation',
            desc: "No judgment, just honesty. Tell me your budget, your vibe, and your must-haves. I'll work with what you have and go from there."
          }, {
            icon: <CompassIcon data-id="element-666" />,
            title: '2. I Build Your Perfect Trip',
            desc: 'I research, compare, and put together a personalized travel plan that fits your budget—flights, stays, activities, all of it.'
          }, {
            icon: <PlaneIcon data-id="element-667" />,
            title: '3. You Pack Your Bags & Go',
            desc: 'You get your itinerary, book with confidence, and go live your best life. Come back recharged and ready.'
          }].map((step, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.2
          }} className="relative z-10" data-id="element-668">
                <div className="w-24 h-24 mx-auto bg-white rounded-full shadow-sm border border-blush-100 flex items-center justify-center text-cta-500 mb-6" data-id="element-669">
                  {cloneElement(step.icon as React.ReactElement, {
                className: 'w-10 h-10'
              })}
                </div>
                <h3 className="font-heading text-2xl font-bold text-plum-700 mb-3" data-id="element-670">
                  {step.title}
                </h3>
                <p className="text-plum-500 font-medium" data-id="element-671">{step.desc}</p>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Travel Types */}
      <section className="py-14 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-672">
        <div className="text-center mb-16" data-id="element-673">
          <h2 className="font-heading text-4xl font-bold text-plum-700 mb-4" data-id="element-674">
            What We Can Plan
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-675">
          {[{
          title: 'Budget Getaways',
          desc: 'Weekend trips and local escapes under $500.',
          color: 'bg-blush-50 border-blush-100'
        }, {
          title: 'Healing Retreats',
          desc: 'Wellness-focused travel experiences to reset your soul.',
          color: 'bg-lavender-50 border-lavender-100'
        }, {
          title: 'Adventure Trips',
          desc: 'For when you need to feel alive and push your boundaries.',
          color: 'bg-sky-50 border-sky-100'
        }, {
          title: 'Group Travel',
          desc: 'Coming soon: group trips with the community. We rise together.',
          color: 'bg-white border-plum-100',
          badge: 'Coming Soon'
        }].map((type, i) => <motion.div key={i} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className={`p-8 rounded-3xl border ${type.color} shadow-sm relative`} data-id="element-676">
              {type.badge && <div className="absolute top-4 right-4 px-2 py-1 bg-plum-100 text-plum-700 text-xs font-bold rounded-md uppercase" data-id="element-677">
                  {type.badge}
                </div>}
              <SparklesIcon className="w-6 h-6 text-cta-500 mb-4" data-id="element-678" />
              <h3 className="font-heading text-xl font-bold text-plum-700 mb-2" data-id="element-679">
                {type.title}
              </h3>
              <p className="text-plum-500 font-medium text-sm" data-id="element-680">{type.desc}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Travel Questionnaire Form */}
      <section className="py-12 relative overflow-hidden bg-gradient-to-b from-white to-blush-50" data-id="element-681">
        <SparkleEffect count={80} data-id="element-682" />
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-683">
          <div className="text-center mb-12" data-id="element-684">
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-plum-700 mb-4 flex items-center justify-center gap-3" data-id="element-685">
              Let Me Plan Your Escape ✈️
            </h2>
            <p className="text-lg text-plum-500 font-medium max-w-2xl mx-auto" data-id="element-686">
              Tell me what you're looking for—even if you don't know exactly
              where you want to go yet. Give me your budget, your vibe, and your
              must-haves… and I'll build something just for you 💕
            </p>
          </div>

          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} className="bg-white/60 backdrop-blur-md p-8 md:p-12 rounded-[3rem] shadow-xl border border-white relative" data-id="element-687">
            <div className="absolute -top-6 -right-6 rotate-12" data-id="element-688">
              <GlitterHeart size={40} className="opacity-80" data-id="element-689" />
            </div>
            <div className="absolute -bottom-4 -left-4 -rotate-12" data-id="element-690">
              <GlitterHeart size={30} variant="light" className="opacity-60" data-id="element-691" />
            </div>

            <form onSubmit={handleSubmit} className="space-y-6 relative z-10" data-id="element-692">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-693">
                {/* Name */}
                <div className="md:col-span-2" data-id="element-694">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-695">
                    Name
                  </label>
                  <input type="text" name="name" required value={formData.name} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Your name" data-id="element-696" />
                </div>

                {/* Contact Preference */}
                <div className="md:col-span-2" data-id="element-697">
                  <label className="block text-sm font-bold text-plum-700 mb-3" data-id="element-698">
                    Best way to contact you
                  </label>
                  <div className="flex flex-wrap gap-4" data-id="element-699">
                    {['Phone Call', 'Text Message', 'Email'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer group" data-id="element-700">
                        <input type="radio" name="contactPref" value={option} required onChange={handleInputChange} className="w-5 h-5 text-cta-500 border-blush-300 focus:ring-cta-500 cursor-pointer" data-id="element-701" />
                        <span className="text-plum-600 font-medium group-hover:text-plum-800 transition-colors" data-id="element-702">
                          {option}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Contact Details */}
                <div className="md:col-span-2" data-id="element-703">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-704">
                    Enter your contact details
                  </label>
                  <input type="text" name="contactDetails" required value={formData.contactDetails} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Phone number or email" data-id="element-705" />
                </div>

                {/* Destination */}
                <div className="md:col-span-2" data-id="element-706">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-707">
                    Where are you thinking about going? (optional)
                  </label>
                  <input type="text" name="destination" value={formData.destination} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="E.g., Mexico, Europe, or 'Anywhere warm!'" data-id="element-708" />
                </div>

                {/* Trip Type */}
                <div className="md:col-span-2" data-id="element-709">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-710">
                    What kind of trip are you looking for?
                  </label>
                  <input type="text" name="tripType" required value={formData.tripType} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Relaxing, adventure, cruise, romantic, family, solo, etc." data-id="element-711" />
                </div>

                {/* Budget */}
                <div data-id="element-712">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-713">
                    Budget Range
                  </label>
                  <select name="budget" required value={formData.budget} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all text-plum-700" data-id="element-714">
                    <option value="" disabled data-id="element-715">
                      Select a budget
                    </option>
                    <option value="Under $500" data-id="element-716">Under $500</option>
                    <option value="$500–$1000" data-id="element-717">$500–$1000</option>
                    <option value="$1000+" data-id="element-718">$1000+</option>
                    <option value="Custom" data-id="element-719">Custom</option>
                  </select>
                </div>

                {/* Travelers */}
                <div data-id="element-720">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-721">
                    Who is traveling?
                  </label>
                  <select name="travelers" required value={formData.travelers} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all text-plum-700" data-id="element-722">
                    <option value="" disabled data-id="element-723">
                      Select travelers
                    </option>
                    <option value="Just me" data-id="element-724">Just me</option>
                    <option value="Couple" data-id="element-725">Couple</option>
                    <option value="Family" data-id="element-726">Family</option>
                    <option value="Group" data-id="element-727">Group</option>
                  </select>
                </div>

                {/* Dates */}
                <div className="md:col-span-2" data-id="element-728">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-729">
                    Travel Dates or Flexibility
                  </label>
                  <input type="text" name="dates" required value={formData.dates} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Exact dates or 'flexible in October'" data-id="element-730" />
                </div>

                {/* Budget-based option */}
                <div className="md:col-span-2 bg-blush-50/80 p-5 rounded-2xl border border-blush-100" data-id="element-731">
                  <label className="block text-sm font-bold text-plum-800 mb-3" data-id="element-732">
                    Are you looking for a budget-based trip where I choose the
                    destination based on your budget?
                  </label>
                  <div className="flex gap-6" data-id="element-733">
                    {['Yes', 'No'].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer group" data-id="element-734">
                        <input type="radio" name="budgetBased" value={option} required onChange={handleInputChange} className="w-5 h-5 text-cta-500 border-blush-300 focus:ring-cta-500 cursor-pointer" data-id="element-735" />
                        <span className="text-plum-700 font-bold group-hover:text-plum-900 transition-colors" data-id="element-736">
                          {option}
                        </span>
                      </label>)}
                  </div>
                </div>

                {/* Priorities */}
                <div className="md:col-span-2" data-id="element-737">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-738">
                    What matters most for this trip?
                  </label>
                  <input type="text" name="priorities" required value={formData.priorities} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all" placeholder="Price, luxury, experience, location, etc." data-id="element-739" />
                </div>

                {/* Extra Details */}
                <div className="md:col-span-2" data-id="element-740">
                  <label className="block text-sm font-bold text-plum-700 mb-2" data-id="element-741">
                    Anything else you want me to know?
                  </label>
                  <textarea name="extraDetails" rows={4} value={formData.extraDetails} onChange={handleInputChange} className="w-full px-4 py-3 rounded-xl border border-blush-200 focus:ring-2 focus:ring-cta-500 outline-none bg-white/80 transition-all resize-none" placeholder="Any special requests, accessibility needs, or absolute dealbreakers..." data-id="element-742" />
                </div>
              </div>

              <div className="pt-6 text-center" data-id="element-743">
                <button type="submit" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-12 py-4 rounded-full bg-cta-500 text-white font-bold text-xl hover:bg-cta-600 transition-all shadow-xl shadow-cta-500/30 hover:-translate-y-1 hover:scale-105" data-id="element-744">
                  Build My Trip 💖
                </button>
                <p className="mt-4 text-sm font-medium text-plum-500" data-id="element-745">
                  I'll personally review your request and reach out with options
                  that fit your vibe and your budget.
                </p>
              </div>
            </form>
          </motion.div>
        </div>
      </section>
    </div>;
}